import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import BackButton from '@/components/feature/BackButton';

interface MeetingInfo {
  id: string;
  code: string;
  title: string;
  host_name: string;
  created_at: string;
  is_active: boolean;
  scheduled_at: string | null;
}

interface StaffMember {
  id: string;
  email: string;
  prenom: string;
  nom: string;
  role: string;
}

interface Department {
  id: number;
  nom: string;
}

interface RoleOption {
  key: string;
  label: string;
}

interface InvitedGroup {
  role: string;
  roleLabel: string;
  specialite?: string;
}

export default function VisioconferencePage() {
  const { user, role, userRoles } = useAuth();
  const navigate = useNavigate();

  const [joinCode, setJoinCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [canCreate, setCanCreate] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [meetingTitle, setMeetingTitle] = useState('');
  const [meetingDate, setMeetingDate] = useState('');
  const [meetingTime, setMeetingTime] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [activeMeetings, setActiveMeetings] = useState<MeetingInfo[]>([]);
  const [loadingMeetings, setLoadingMeetings] = useState(true);

  // État lien partagé après création
  const [createdMeetingCode, setCreatedMeetingCode] = useState('');
  const [createdMeetingLink, setCreatedMeetingLink] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);

  // Invitations
  const [staffSearch, setStaffSearch] = useState('');
  const [staffResults, setStaffResults] = useState<StaffMember[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedInvites, setSelectedInvites] = useState<StaffMember[]>([]);
  const [showStaffDropdown, setShowStaffDropdown] = useState(false);

  // Groupes (département/rôle)
  const [departments, setDepartments] = useState<Department[]>([]);
  const [roleOptions, setRoleOptions] = useState<RoleOption[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<InvitedGroup[]>([]);
  const [showGroupSelector, setShowGroupSelector] = useState(false);
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedSpecialite, setSelectedSpecialite] = useState('');
  const [groupStaffResults, setGroupStaffResults] = useState<StaffMember[]>([]);
  const [groupSearching, setGroupSearching] = useState(false);
  const [showGroupDropdown, setShowGroupDropdown] = useState(false);

  // Rediriger les patients et les non-connectés
  useEffect(() => {
    if (!user) {
      navigate('/login', { replace: true });
      return;
    }
    if (role === 'patient' || userRoles.includes('patient')) {
      navigate('/', { replace: true });
      return;
    }
  }, [user, role, userRoles, navigate]);

  // Vérifier la permission + charger les réunions
  useEffect(() => {
    if (!user) return;
    checkCreatePermission();
    loadActiveMeetings();
  }, [user]);

  const checkCreatePermission = async () => {
    try {
      const { data, error: fnError } = await supabase.functions.invoke('manage-video-meeting', {
        body: { action: 'check-permission' },
      });

      if (!fnError && data?.canCreate) {
        setCanCreate(true);
        if (data?.isSuperAdmin) setIsSuperAdmin(true);
      }
    } catch {
      // Pas de permission
    }
  };

  const loadActiveMeetings = async () => {
    setLoadingMeetings(true);
    try {
      const { data, error: fnError } = await supabase.functions.invoke('manage-video-meeting', {
        body: { action: 'list-meetings' },
      });

      if (!fnError && data?.meetings) {
        setActiveMeetings(data.meetings);
      }
    } catch {
      // Silencieux
    } finally {
      setLoadingMeetings(false);
    }
  };

  // Recherche de personnel
  const searchStaff = useCallback(async (query: string) => {
    if (query.length < 2) {
      setStaffResults([]);
      setShowStaffDropdown(false);
      return;
    }

    setSearching(true);
    setShowStaffDropdown(true);

    try {
      const { data, error: fnError } = await supabase.functions.invoke('manage-video-meeting', {
        body: { action: 'search-staff', search: query },
      });

      if (!fnError && data?.staff) {
        // Filtrer les déjà sélectionnés
        const filtered = data.staff.filter(
          (s: StaffMember) => !selectedInvites.find((inv) => inv.id === s.id)
        );
        setStaffResults(filtered);
      }
    } catch {
      setStaffResults([]);
    } finally {
      setSearching(false);
    }
  }, [selectedInvites]);

  const addInvite = (member: StaffMember) => {
    if (!selectedInvites.find((s) => s.id === member.id)) {
      setSelectedInvites((prev) => [...prev, member]);
    }
    setStaffSearch('');
    setStaffResults([]);
    setShowStaffDropdown(false);
  };

  const removeInvite = (memberId: string) => {
    setSelectedInvites((prev) => prev.filter((s) => s.id !== memberId));
  };

  // Charger les départements et rôles
  const loadDepartments = async () => {
    try {
      const { data, error: fnError } = await supabase.functions.invoke('manage-video-meeting', {
        body: { action: 'list-departments' },
      });
      if (!fnError && data) {
        setDepartments(data.departments || []);
        setRoleOptions(data.roles || []);
      }
    } catch {
      // Silencieux
    }
  };

  // Rechercher par groupe (rôle + spécialité)
  const searchByGroup = async (role: string, specialite: string, search: string) => {
    if (!role) return;
    setGroupSearching(true);
    setShowGroupDropdown(true);
    try {
      const body: Record<string, any> = { action: 'search-by-group', role };
      if (specialite) body.specialite = specialite;
      if (search.trim()) body.search = search.trim();
      const { data, error: fnError } = await supabase.functions.invoke('manage-video-meeting', { body });
      if (!fnError && data?.staff) {
        setGroupStaffResults(data.staff);
      }
    } catch {
      setGroupStaffResults([]);
    } finally {
      setGroupSearching(false);
    }
  };

  const addGroup = (spec?: string) => {
    const roleLabel = roleOptions.find(r => r.key === selectedRole)?.label || selectedRole;
    const newGroup: InvitedGroup = { role: selectedRole, roleLabel, specialite: spec || undefined };
    // Vérifier si le groupe existe déjà
    const exists = selectedGroups.find(g => g.role === newGroup.role && g.specialite === newGroup.specialite);
    if (!exists) {
      setSelectedGroups(prev => [...prev, newGroup]);
    }
    // Réinitialiser
    setSelectedRole('');
    setSelectedSpecialite('');
    setGroupStaffResults([]);
    setShowGroupDropdown(false);
    setShowGroupSelector(false);
  };

  const removeGroup = (index: number) => {
    setSelectedGroups(prev => prev.filter((_, i) => i !== index));
  };

  // Obtenir la date min pour le datepicker (aujourd'hui)
  const getTodayStr = () => {
    const d = new Date();
    return d.toISOString().split('T')[0];
  };

  const handleJoinMeeting = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const code = joinCode.trim().toUpperCase();
    if (!code) {
      setError('Veuillez entrer un code de réunion');
      return;
    }

    setLoading(true);
    try {
      const { data, error: fnError } = await supabase.functions.invoke('manage-video-meeting', {
        body: { action: 'join-meeting', code },
      });

      if (fnError || data?.error) {
        setError(data?.error || 'Réunion introuvable. Vérifiez le code.');
        return;
      }

      navigate(`/visioconference/reunion/${code}`);
    } catch {
      setError('Erreur lors de la connexion à la réunion.');
    } finally {
      setLoading(false);
    }
  }, [joinCode, navigate]);

  const handleCreateMeeting = async () => {
    setCreateError('');
    if (!meetingTitle.trim()) {
      setCreateError('Veuillez entrer un titre pour la réunion');
      return;
    }

    setCreating(true);
    try {
      let scheduledAt = null;
      if (meetingDate && meetingTime) {
        scheduledAt = new Date(`${meetingDate}T${meetingTime}:00`).toISOString();
      } else if (meetingDate) {
        scheduledAt = new Date(`${meetingDate}T09:00:00`).toISOString();
      }

      const body: Record<string, any> = {
        action: 'create-meeting',
        title: meetingTitle.trim(),
        scheduledAt,
      };

      if (selectedInvites.length > 0) {
        body.invitedUserIds = selectedInvites.map((s) => s.id);
      }

      if (selectedGroups.length > 0) {
        body.invitedGroups = selectedGroups.map((g) => ({
          role: g.role,
          specialite: g.specialite,
        }));
      }

      const { data, error: fnError } = await supabase.functions.invoke('manage-video-meeting', {
        body,
      });

      if (fnError || data?.error) {
        setCreateError(data?.error || 'Erreur lors de la création');
        return;
      }

      if (data?.meeting?.code) {
        // ⭐ Afficher l'écran de succès avec le lien partageable au lieu de naviguer directement
        const basePath = __BASE_PATH__.split('/').filter(Boolean).join('/');
        const pathPrefix = basePath ? `/${basePath}` : '';
        const link = `${window.location.origin}${pathPrefix}${data.shareableLink || `/visioconference/reunion/${data.meeting.code}`}`;
        setCreatedMeetingCode(data.meeting.code);
        setCreatedMeetingLink(link);
        setShowCreateModal(false);
        // Ne pas reset le formulaire tout de suite pour garder l'info en arrière-plan
      }
    } catch {
      setCreateError('Erreur serveur. Réessayez.');
    } finally {
      setCreating(false);
    }
  };

  const resetCreateForm = () => {
    setMeetingTitle('');
    setMeetingDate('');
    setMeetingTime('');
    setStaffSearch('');
    setStaffResults([]);
    setSelectedInvites([]);
    setSelectedGroups([]);
    setSelectedRole('');
    setSelectedSpecialite('');
    setGroupStaffResults([]);
    setShowGroupSelector(false);
    setShowGroupDropdown(false);
    setCreateError('');
    setCreatedMeetingCode('');
    setCreatedMeetingLink('');
    setLinkCopied(false);
  };

  // ⭐ Partage et actions sur le lien créé
  const copyMeetingLink = () => {
    navigator.clipboard.writeText(createdMeetingLink).then(
      () => { setLinkCopied(true); setTimeout(() => setLinkCopied(false), 2000); },
      () => {
        const input = document.createElement('input');
        input.value = createdMeetingLink;
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
      },
    );
  };

  const shareWhatsApp = () => {
    const text = encodeURIComponent(`Rejoins ma visioconférence Médicale : ${createdMeetingLink}\nCode : ${createdMeetingCode}`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const shareByEmail = () => {
    const subject = encodeURIComponent('Invitation à une visioconférence');
    const body = encodeURIComponent(`Bonjour,\n\nVous êtes invité(e) à rejoindre une visioconférence.\n\nLien : ${createdMeetingLink}\nCode : ${createdMeetingCode}\n\nÀ bientôt !`);
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
  };

  const joinCreatedMeeting = () => {
    navigate(`/visioconference/reunion/${createdMeetingCode}`);
  };

  const formatMeetingDateTime = (meeting: MeetingInfo) => {
    if (meeting.scheduled_at) {
      const d = new Date(meeting.scheduled_at);
      return (
        d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }) +
        ' à ' +
        d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
      );
    }
    const diff = Date.now() - new Date(meeting.created_at).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "À l'instant";
    if (mins < 60) return `Il y a ${mins} min`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `Il y a ${hours}h`;
    const d = new Date(meeting.created_at);
    return (
      d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }) +
      ' à ' +
      d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
    );
  };

  if (!user || role === 'patient' || userRoles.includes('patient')) return null;

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />

      <main className="flex-1 py-10 px-4 sm:px-6 lg:px-8 min-w-0">
        <div className="max-w-4xl mx-auto min-w-0">
          <div className="mb-6">
            <BackButton />
          </div>

          {/* En-tête */}
          <div className="text-center mb-10">
            <div className="w-20 h-20 bg-clinic-blue/10 rounded-2xl flex items-center justify-center mx-auto mb-5">
              <i className="ri-vidicon-line text-4xl text-clinic-blue"></i>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-clinic-blue-dark mb-3">
              Visioconférence
            </h1>
            <p className="text-gray-600 max-w-lg mx-auto">
              Rejoignez une réunion avec un code ou créez-en une nouvelle si vous y êtes autorisé.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* Colonne gauche : Rejoindre une réunion */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-2xl p-4 md:p-8 border-2 border-clinic-blue/10 shadow-sm">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-clinic-green/10 rounded-xl flex items-center justify-center">
                    <i className="ri-login-circle-line text-xl text-clinic-green"></i>
                  </div>
                  <h2 className="text-xl font-semibold text-clinic-blue-dark">Rejoindre une réunion</h2>
                </div>

                <form onSubmit={handleJoinMeeting}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Code de la réunion
                  </label>
                  <div className="flex flex-col md:flex-row gap-3 min-w-0">
                    <input
                      type="text"
                      value={joinCode}
                      onChange={(e) => {
                        setJoinCode(e.target.value.toUpperCase());
                        setError('');
                      }}
                      placeholder="Ex: ABC12345"
                      maxLength={8}
                      className="flex-1 min-w-0 px-4 py-3 text-lg tracking-widest font-mono text-center bg-gray-50 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-clinic-blue/40 transition-colors placeholder:text-sm placeholder:tracking-normal placeholder:font-sans"
                      autoComplete="off"
                    />
                    <button
                      type="submit"
                      disabled={loading || !joinCode.trim()}
                      className="w-full md:w-auto px-4 md:px-6 py-3 bg-clinic-green text-white rounded-xl hover:bg-clinic-green-dark transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shrink-0"
                    >
                      {loading ? (
                        <>
                          <i className="ri-loader-4-line animate-spin"></i>
                          Connexion...
                        </>
                      ) : (
                        <>
                          <i className="ri-login-circle-line"></i>
                          <span className="md:hidden">Rejoindre</span>
                          <span className="hidden md:inline">Rejoindre</span>
                        </>
                      )}
                    </button>
                  </div>
                  {error && (
                    <p className="mt-3 text-sm text-red-500 flex items-center gap-1.5">
                      <i className="ri-error-warning-line"></i>
                      {error}
                    </p>
                  )}
                </form>

                {/* Réunions actives */}
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <div className="flex items-center justify-between mb-4 min-w-0">
                    <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2 shrink-0">
                      <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                      Réunions en cours
                    </h3>
                    <button
                      onClick={loadActiveMeetings}
                      className="text-xs text-gray-500 hover:text-gray-700 transition-colors flex items-center gap-1 cursor-pointer shrink-0"
                    >
                      <i className="ri-refresh-line"></i>
                      Actualiser
                    </button>
                  </div>

                  {loadingMeetings ? (
                    <div className="text-center py-6">
                      <i className="ri-loader-4-line animate-spin text-2xl text-gray-400"></i>
                    </div>
                  ) : activeMeetings.length === 0 ? (
                    <div className="text-center py-6 bg-gray-50 rounded-xl">
                      <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-2">
                        <i className="ri-video-off-line text-gray-400"></i>
                      </div>
                      <p className="text-sm text-gray-500">Aucune réunion en cours</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {activeMeetings.map((meeting) => (
                        <button
                          key={meeting.id}
                          onClick={() => navigate(`/visioconference/reunion/${meeting.code}`)}
                          className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-clinic-green/5 rounded-xl transition-colors border border-gray-200 hover:border-clinic-green/20 cursor-pointer group"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-9 h-9 bg-clinic-green/10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-clinic-green/20 transition-colors">
                              <i className="ri-vidicon-line text-clinic-green"></i>
                            </div>
                            <div className="text-left min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{meeting.title}</p>
                              <p className="text-xs text-gray-500 truncate">
                                {meeting.host_name} · {formatMeetingDateTime(meeting)}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 flex-shrink-0 ml-3">
                            <span className="text-xs font-mono font-semibold text-clinic-green-dark bg-clinic-green/10 px-2 py-0.5 rounded-md hidden sm:inline">
                              {meeting.code}
                            </span>
                            <i className="ri-arrow-right-s-line text-gray-400 group-hover:text-clinic-green transition-colors"></i>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Colonne droite : Créer une réunion */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl p-4 md:p-8 border-2 border-clinic-blue/10 shadow-sm lg:sticky lg:top-24">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 bg-clinic-blue/10 rounded-xl flex items-center justify-center">
                    <i className="ri-add-circle-line text-xl text-clinic-blue"></i>
                  </div>
                  <h2 className="text-xl font-semibold text-clinic-blue-dark">Créer une réunion</h2>
                </div>

                {canCreate ? (
                  <>
                    {createdMeetingLink ? (
                      /* ⭐ Écran de succès : lien créé et partageable */
                      <div className="text-center">
                        <div className="w-14 h-14 bg-clinic-green/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                          <i className="ri-check-line text-3xl text-clinic-green"></i>
                        </div>
                        <h3 className="text-base font-semibold text-clinic-blue-dark mb-2">Lien créé !</h3>
                        <p className="text-xs text-gray-600 mb-4 max-w-xs mx-auto">
                          Partagez ce lien. Les personnes qui cliquent dessus rejoignent directement, avec ou sans compte.
                        </p>

                        <div className="bg-gray-50 border-2 border-clinic-green/30 rounded-2xl p-3 mb-4">
                          <div className="flex items-center justify-between gap-2 mb-2">
                            <span className="text-xs font-mono font-bold text-clinic-blue-dark tracking-wider break-all select-all text-left">
                              {createdMeetingCode}
                            </span>
                            <span className="text-[10px] text-gray-400 bg-gray-200 px-1.5 py-0.5 rounded whitespace-nowrap">CODE</span>
                          </div>
                          <p className="text-[11px] font-mono text-clinic-blue-dark break-all select-all text-left bg-white rounded-lg p-2 border border-gray-100">
                            {createdMeetingLink}
                          </p>
                        </div>

                        <div className="space-y-2">
                          <div className="grid grid-cols-2 gap-2">
                            <button
                              onClick={shareWhatsApp}
                              className="px-3 py-2.5 bg-[#25D366] hover:bg-[#20bd5a] text-white rounded-xl transition-colors font-medium text-xs flex items-center justify-center gap-1.5 whitespace-nowrap cursor-pointer"
                            >
                              <i className="ri-whatsapp-line text-base"></i>
                              WhatsApp
                            </button>
                            <button
                              onClick={shareByEmail}
                              className="px-3 py-2.5 bg-clinic-blue hover:bg-clinic-blue/90 text-white rounded-xl transition-colors font-medium text-xs flex items-center justify-center gap-1.5 whitespace-nowrap cursor-pointer"
                            >
                              <i className="ri-mail-line text-base"></i>
                              Email
                            </button>
                          </div>
                          <button
                            onClick={copyMeetingLink}
                            className="w-full px-3 py-2.5 border-2 border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium text-xs flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                          >
                            <i className={`${linkCopied ? 'ri-check-line text-clinic-green' : 'ri-file-copy-line'}`}></i>
                            {linkCopied ? 'Copié !' : 'Copier le lien'}
                          </button>
                          <button
                            onClick={joinCreatedMeeting}
                            className="w-full px-4 py-3 bg-clinic-green hover:bg-clinic-green-dark text-white rounded-xl transition-all font-semibold text-sm flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer mt-1"
                          >
                            <i className="ri-login-circle-line text-lg"></i>
                            Rejoindre la réunion
                          </button>
                          <button
                            onClick={() => {
                              setCreatedMeetingCode('');
                              setCreatedMeetingLink('');
                            }}
                            className="w-full px-3 py-2 text-xs text-gray-500 hover:text-gray-700 font-medium whitespace-nowrap cursor-pointer"
                          >
                            Créer une autre réunion
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <p className="text-sm text-gray-600 mb-5">
                          {isSuperAdmin
                            ? "En tant qu'administrateur, vous pouvez créer des réunions et gérer les permissions des autres utilisateurs."
                            : "Vous avez l'autorisation de créer des réunions. Générez un code à partager avec vos collègues."
                          }
                        </p>
                        <button
                          onClick={() => { setShowCreateModal(true); loadDepartments(); }}
                          className="w-full px-5 py-3.5 bg-clinic-blue text-white rounded-xl hover:bg-clinic-blue/90 transition-colors font-semibold flex items-center justify-center gap-2 whitespace-nowrap"
                        >
                          <i className="ri-add-line text-lg"></i>
                          Nouvelle réunion
                        </button>
                      </>
                    )}
                  </>
                ) : (
                  <div className="text-center py-6 bg-gray-50 rounded-xl">
                    <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <i className="ri-lock-line text-xl text-gray-400"></i>
                    </div>
                    <p className="text-sm text-gray-600 mb-1">Création restreinte</p>
                    <p className="text-xs text-gray-500 max-w-xs mx-auto">
                      Seul l&apos;administrateur peut autoriser un compte à créer des réunions. Contactez votre administrateur pour obtenir cette permission.
                    </p>
                  </div>
                )}

                {/* Infos pratiques */}
                <div className="mt-6 pt-5 border-t border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Comment ça marche ?</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2.5 text-sm text-gray-600">
                      <div className="w-6 h-6 rounded-full bg-clinic-blue/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-xs font-bold text-clinic-blue">1</span>
                      </div>
                      <span>Recevez un code de réunion de la part de l&apos;organisateur</span>
                    </li>
                    <li className="flex items-start gap-2.5 text-sm text-gray-600">
                      <div className="w-6 h-6 rounded-full bg-clinic-blue/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-xs font-bold text-clinic-blue">2</span>
                      </div>
                      <span>Entrez le code pour rejoindre instantanément</span>
                    </li>
                    <li className="flex items-start gap-2.5 text-sm text-gray-600">
                      <div className="w-6 h-6 rounded-full bg-clinic-blue/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-xs font-bold text-clinic-blue">3</span>
                      </div>
                      <span>L&apos;hôte contrôle les micros, le partage d&apos;écran et la réunion</span>
                    </li>
                  </ul>
                </div>

                {/* Lien rapide Meet */}
                <div className="mt-5 pt-4 border-t border-gray-100">
                  <a
                    href="/meet"
                    onClick={(e) => { e.preventDefault(); navigate('/meet'); }}
                    className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-clinic-green/5 to-clinic-green/10 rounded-xl border border-clinic-green/20 hover:border-clinic-green/40 transition-all group cursor-pointer"
                  >
                    <div className="w-9 h-9 bg-clinic-green/15 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-clinic-green/25 transition-colors">
                      <i className="ri-whatsapp-line text-lg text-clinic-green"></i>
                    </div>
                    <div className="text-left min-w-0">
                      <p className="text-sm font-semibold text-clinic-green-dark">Lien rapide</p>
                      <p className="text-xs text-gray-600">Créez un lien partageable sur WhatsApp, sans compte requis</p>
                    </div>
                    <i className="ri-arrow-right-s-line text-clinic-green ml-auto flex-shrink-0 group-hover:translate-x-0.5 transition-transform"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />

      {/* Modal de création de réunion (version enrichie) */}
      {showCreateModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => {
              setShowCreateModal(false);
              resetCreateForm();
            }}
          ></div>
          <div className="relative bg-white rounded-2xl p-6 md:p-8 w-full max-w-lg shadow-2xl animate-fadeIn max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-clinic-blue-dark">Nouvelle réunion</h3>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetCreateForm();
                }}
                className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-gray-500"></i>
              </button>
            </div>

            {/* Titre */}
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Titre de la réunion <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={meetingTitle}
              onChange={(e) => {
                setMeetingTitle(e.target.value);
                setCreateError('');
              }}
              placeholder="Ex: Briefing du service pédiatrie"
              maxLength={100}
              className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-clinic-blue/40 transition-colors text-sm"
              autoFocus
            />

            {/* Date et Heure */}
            <div className="mt-5">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Date et heure (optionnel)
              </label>
              <p className="text-xs text-gray-500 mb-3">
                Programmez la réunion à l&apos;avance. Laissez vide pour une réunion immédiate.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Date</label>
                  <input
                    type="date"
                    value={meetingDate}
                    min={getTodayStr()}
                    onChange={(e) => setMeetingDate(e.target.value)}
                    className="w-full px-3 py-2.5 bg-gray-50 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-clinic-blue/40 transition-colors text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Heure</label>
                  <input
                    type="time"
                    value={meetingTime}
                    onChange={(e) => setMeetingTime(e.target.value)}
                    className="w-full px-3 py-2.5 bg-gray-50 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-clinic-blue/40 transition-colors text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Inviter du personnel */}
            <div className="mt-5">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Inviter du personnel (optionnel)
              </label>
              <p className="text-xs text-gray-500 mb-3">
                Recherchez et sélectionnez les membres du personnel à inviter.
              </p>

              {/* Barre de recherche */}
              <div className="relative">
                <div className="flex items-center gap-2 px-3 py-2.5 bg-gray-50 border-2 border-gray-200 rounded-xl focus-within:border-clinic-blue/40 transition-colors">
                  <i className="ri-search-line text-gray-400 flex-shrink-0"></i>
                  <input
                    type="text"
                    value={staffSearch}
                    onChange={(e) => {
                      setStaffSearch(e.target.value);
                      searchStaff(e.target.value);
                    }}
                    onFocus={() => {
                      if (staffResults.length > 0) setShowStaffDropdown(true);
                    }}
                    placeholder="Rechercher par nom ou email..."
                    className="flex-1 bg-transparent outline-none text-sm"
                  />
                  {searching && (
                    <i className="ri-loader-4-line animate-spin text-gray-400 flex-shrink-0"></i>
                  )}
                </div>

                {/* Dropdown des résultats */}
                {showStaffDropdown && staffResults.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-30 max-h-48 overflow-y-auto">
                    {staffResults.map((member) => (
                      <button
                        key={member.id}
                        onClick={() => addInvite(member)}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left cursor-pointer border-b border-gray-100 last:border-b-0"
                      >
                        <div className="w-8 h-8 bg-clinic-blue/10 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-semibold text-clinic-blue">
                            {(member.prenom?.[0] || '') + (member.nom?.[0] || '')}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {member.prenom} {member.nom}
                          </p>
                          <p className="text-xs text-gray-500 truncate">{member.email}</p>
                        </div>
                        <i className="ri-add-circle-line text-clinic-blue ml-auto flex-shrink-0"></i>
                      </button>
                    ))}
                  </div>
                )}

                {showStaffDropdown && staffSearch.length >= 2 && staffResults.length === 0 && !searching && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-30 p-4 text-center">
                    <p className="text-sm text-gray-500">Aucun membre du personnel trouvé</p>
                  </div>
                )}
              </div>

              {/* Liste des invités sélectionnés */}
              {selectedInvites.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {selectedInvites.map((member) => (
                    <span
                      key={member.id}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-clinic-blue/5 border border-clinic-blue/20 rounded-full text-sm text-clinic-blue-dark"
                    >
                      <span className="font-medium">
                        {member.prenom} {member.nom}
                      </span>
                      <button
                        onClick={() => removeInvite(member.id)}
                        className="w-5 h-5 rounded-full hover:bg-clinic-blue/10 flex items-center justify-center cursor-pointer transition-colors"
                      >
                        <i className="ri-close-line text-xs"></i>
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Inviter par département / rôle */}
            <div className="mt-5 pt-4 border-t border-gray-100">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">
                  Inviter par département ou rôle
                </label>
                {!showGroupSelector && (
                  <button
                    onClick={() => setShowGroupSelector(true)}
                    className="text-xs text-clinic-blue hover:text-clinic-blue-dark font-medium flex items-center gap-1 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-add-line"></i>
                    Ajouter un groupe
                  </button>
                )}
              </div>
              <p className="text-xs text-gray-500 mb-3">
                Invitez tout un service (ex: Cardiologie) ou une catégorie de personnel (ex: tous les infirmiers).
              </p>

              {/* Groupes déjà sélectionnés */}
              {selectedGroups.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {selectedGroups.map((group, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-full text-sm text-amber-800"
                    >
                      <i className="ri-group-line text-xs"></i>
                      <span className="font-medium">
                        {group.specialite ? `${group.specialite} (${group.roleLabel})` : group.roleLabel}
                      </span>
                      <button
                        onClick={() => removeGroup(idx)}
                        className="w-5 h-5 rounded-full hover:bg-amber-100 flex items-center justify-center cursor-pointer transition-colors"
                      >
                        <i className="ri-close-line text-xs"></i>
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {/* Sélecteur de groupe */}
              {showGroupSelector && (
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  {/* Choix du rôle */}
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">Rôle</label>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {roleOptions.map((r) => (
                      <button
                        key={r.key}
                        onClick={() => {
                          setSelectedRole(r.key);
                          setSelectedSpecialite('');
                          setGroupStaffResults([]);
                          searchByGroup(r.key, '', '');
                        }}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                          selectedRole === r.key
                            ? 'bg-clinic-blue text-white'
                            : 'bg-white border border-gray-200 text-gray-600 hover:border-clinic-blue/30'
                        }`}
                      >
                        {r.label}
                      </button>
                    ))}
                  </div>

                  {/* Choix de la spécialité (seulement si médecin ou sage-femme sélectionné) */}
                  {(selectedRole === 'medecins' || selectedRole === 'sage_femmes') && departments.length > 0 && (
                    <>
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">
                        Spécialité / Département <span className="text-gray-400 font-normal">(optionnel)</span>
                      </label>
                      <div className="flex flex-wrap gap-1.5 mb-3 max-h-32 overflow-y-auto">
                        <button
                          onClick={() => {
                            setSelectedSpecialite('');
                            searchByGroup(selectedRole, '', '');
                          }}
                          className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                            !selectedSpecialite
                              ? 'bg-clinic-green text-white'
                              : 'bg-white border border-gray-200 text-gray-600 hover:border-clinic-green/30'
                          }`}
                        >
                          Tous
                        </button>
                        {departments.map((dep) => (
                          <button
                            key={dep.id}
                            onClick={() => {
                              setSelectedSpecialite(dep.nom);
                              searchByGroup(selectedRole, dep.nom, '');
                            }}
                            className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                              selectedSpecialite === dep.nom
                                ? 'bg-clinic-green text-white'
                                : 'bg-white border border-gray-200 text-gray-600 hover:border-clinic-green/30'
                            }`}
                          >
                            {dep.nom}
                          </button>
                        ))}
                      </div>
                    </>
                  )}

                  {/* Aperçu du nombre de personnes */}
                  {selectedRole && (
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-gray-500">
                        {groupSearching ? (
                          <span className="flex items-center gap-1"><i className="ri-loader-4-line animate-spin"></i>Recherche...</span>
                        ) : groupStaffResults.length > 0 ? (
                          <>
                            <i className="ri-user-line mr-1"></i>
                            {groupStaffResults.length} personne{groupStaffResults.length > 1 ? 's' : ''} trouvée{groupStaffResults.length > 1 ? 's' : ''}
                          </>
                        ) : (
                          'Sélectionnez un rôle'
                        )}
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setShowGroupSelector(false);
                            setSelectedRole('');
                            setSelectedSpecialite('');
                            setGroupStaffResults([]);
                          }}
                          className="px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700 font-medium cursor-pointer whitespace-nowrap"
                        >
                          Annuler
                        </button>
                        <button
                          onClick={() => addGroup(selectedSpecialite || undefined)}
                          disabled={!selectedRole}
                          className="px-3 py-1.5 bg-clinic-blue text-white rounded-lg hover:bg-clinic-blue/90 transition-colors text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-check-line mr-1"></i>
                          Ajouter ce groupe
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Message d'erreur */}
            {createError && (
              <p className="mt-4 text-sm text-red-500 flex items-center gap-1.5">
                <i className="ri-error-warning-line"></i>
                {createError}
              </p>
            )}

            {/* Boutons */}
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetCreateForm();
                }}
                className="flex-1 px-4 py-2.5 border-2 border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium text-sm whitespace-nowrap"
              >
                Annuler
              </button>
              <button
                onClick={handleCreateMeeting}
                disabled={creating || !meetingTitle.trim()}
                className="flex-1 px-4 py-2.5 bg-clinic-blue text-white rounded-xl hover:bg-clinic-blue/90 transition-colors font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap flex items-center justify-center gap-2"
              >
                {creating ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Création...
                  </>
                ) : (
                  <>
                    <i className="ri-add-line"></i>
                    Créer la réunion
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Overlay pour fermer le dropdown de recherche */}
      {(showStaffDropdown || showGroupDropdown) && (
        <div
          className="fixed inset-0 z-20"
          onClick={() => { setShowStaffDropdown(false); setShowGroupDropdown(false); }}
        ></div>
      )}
    </div>
  );
}